import { isValid, getFormValues } from 'redux-form';
import { createValidator } from 'utils/formValidations';
import purchaserValidations from 'utils/purchaserValidations';

/**
 * Client-side validity of the purchaser form, independent of redux-form field registration.
 *
 * redux-form's `isValid` only counts errors for currently-registered fields, so it reports the
 * form as valid when it is unmounted (the wallet flow can re-render it away) even though the
 * persisted values are invalid. That let the Google Pay / Apple Pay sheet open with invalid data.
 * To gate reliably we also re-run the form-level validator against the persisted values, which
 * survive unmount (`destroyOnUnmount: false`).
 *
 * The error filter mirrors which fields the form actually renders, since a field that is not
 * shown is never collected and must not be treated as required here (otherwise it would block the
 * wallet for a configuration where it is intentionally absent). Keep this in sync with
 * `PurchaserFields`:
 * - `legalTerms`: handled separately via `requiresTermsValidation`.
 * - `email`: only shown (and required) when `SHOW_PURCHASER_EMAIL_FIELD`.
 * - `purchaserLastName`: not rendered when the tenant collects a business name (NIT +
 *   `ACCEPT_PURCHASER_BUSINESS_NAME`); the business name goes into `purchaserFirstName`.
 *
 * Coverage caveat: this only re-runs the FORM-LEVEL validator (`purchaserValidations`). The
 * field-level `validate` props on the purchaser form — `documentId` (PurchaserFields) and the
 * AddressFields (`zipCode`, `streetName`, `streetNumber`, `neighborhood`, `city`, `country`) — are
 * NOT mirrored here. While the form is mounted `isValid('purchaser')` still catches them; the only
 * gap is the unmounted window, and only for the few tenants that render those fields. If a wallet
 * must hard-block on documentId/address validity, add those validators here too.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean} True when the purchaser form values are client-side valid.
 */
export default function selectIsPurchaserFormValid(state) {
  const features = state?.whitelabelConfig?.features || {};
  const values = getFormValues('purchaser')(state) || {};
  const usesBusinessName =
    Boolean(features.ACCEPT_PURCHASER_BUSINESS_NAME) && values.documentType === 'NIT';

  const errorKeys = Object.keys(createValidator(purchaserValidations)(values)).filter((key) => {
    if (key === 'legalTerms') return false;
    if (key === 'email') return Boolean(features.SHOW_PURCHASER_EMAIL_FIELD);
    if (key === 'purchaserLastName') return !usesBusinessName;
    return true;
  });

  return isValid('purchaser')(state) && errorKeys.length === 0;
}
